import { memo } from "react";
import PropTypes from "prop-types";

/**
 * ValueDisplayWidget – Detached floating SCADA value display.
 *
 * Renders as a standalone floating widget on the canvas, connected to
 * its parent node via a dashed connector line (WidgetConnector edge).
 *
 * Features:
 * - Large numeric value
 * - Unit label
 * - Status icon (⊙ pressure, ⚡ voltage, etc.)
 * - Industrial monitoring appearance
 * - Threshold color coding
 */
const ValueDisplayWidget = memo(({
  value,
  unit = "",
  width,
  height,
  label = "",
  color = "#3b82f6",
}) => {
  const hasValue = value !== undefined && value !== null && !isNaN(Number(value));
  const numValue = hasValue ? Number(value) : null;

  // Status icon based on unit
  const getIcon = () => {
    switch (unit) {
      case "bar":
      case "psi":
      case "Pa":
      case "kPa":
        return "Ⓟ";
      case "V":
      case "kW":
      case "W":
      case "A":
      case "kWh":
        return "⚡";
      case "°C":
      case "°F":
        return "🌡";
      case "RPM":
      case "Hz":
        return "⟳";
      case "%":
        return "◉";
      case "m³/h":
      case "L/s":
      case "L/min":
        return "≋";
      default:
        return "◈";
    }
  };

  const baseSize = width && height ? Math.sqrt(width * height) * 0.8 : (width || height || 80);

  return (
    <div className="scada-widget scada-widget--value" style={{ width: width || 'auto', height: height || 'auto' }}>
      {/* Icon + label header */}
      <div className="scada-widget__header">
        <span className="scada-widget__icon" style={{ color: "#94a3b8", fontSize: baseSize * 0.25 }}>{getIcon()}</span>
        {label && <span className="scada-widget__label" style={{ color: "#94a3b8", fontSize: baseSize * 0.22, marginLeft: 4 }}>{label}</span>}
        {unit && <span className="scada-widget__unit" style={{ color: "#94a3b8", fontSize: baseSize * 0.22, marginLeft: 'auto' }}>{unit}</span>}
      </div>

      {/* Large value */}
      <div className="scada-widget__value" style={{ fontSize: baseSize * 0.4, color: color }}>
        {hasValue ? numValue.toFixed(1) : "--"}
      </div>
    </div>
  );
});

ValueDisplayWidget.displayName = "ValueDisplayWidget";

ValueDisplayWidget.propTypes = {
  value: PropTypes.number,
  unit: PropTypes.string,
  color: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default ValueDisplayWidget;
